import { useMemo, useState } from 'react'
import { Calculator, Plus, Wand2 } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useCreatePayComponent, usePayComponents, useSetupUsComponents } from '@/features/payroll/queries'
import type { PayComponent } from '@/features/payroll/api'

const COMPONENT_TYPES = [
  { value: 'earning', label: 'Earning' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'employee_tax', label: 'Employee tax' },
  { value: 'employer_tax', label: 'Employer tax' },
  { value: 'employer_contribution', label: 'Employer contribution' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'garnishment', label: 'Garnishment' },
  { value: 'imputed_income', label: 'Imputed income' },
]

const CALC_KINDS = [
  { value: 'fixed', label: 'Fixed amount' },
  { value: 'formula', label: 'Formula (sandboxed)' },
  { value: 'percentage', label: 'Percentage of base' },
  { value: 'hours_x_rate', label: 'Hours × rate' },
  { value: 'federal_withholding', label: 'Federal withholding (IRS Pub 15-T)' },
  { value: 'state_withholding', label: 'State withholding (rule pack)' },
]

const CALC_KIND_NOTES: Record<string, string> = {
  federal_withholding: 'IRS Publication 15-T - uses each employee’s W-4',
  state_withholding: 'State rule pack rate for the employee’s work state',
}

export function PayComponentsListPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const [form, setForm] = useState({
    slug: '',
    label: '',
    component_type: 'earning',
    calc_kind: 'fixed',
    formula: '',
    fixed_amount_minor_units: '',
    rate_pct: '',
    rounding_mode: 'HALF_EVEN',
    visible_on_payslip: true,
  })

  const { data, isLoading } = usePayComponents(typeFilter || undefined)
  const createMut = useCreatePayComponent()
  const setupMut = useSetupUsComponents()
  const canManage = hasPermission('payroll.components.manage')

  async function onSetupUs() {
    try {
      const r = await setupMut.mutateAsync()
      toast({ title: 'US payroll components ready', description: `${r.data.inserted} of ${r.data.total} standard components added.` })
    } catch (err) {
      toast({ title: 'Setup failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }

  const columns = useMemo(
    () => [
      { accessorKey: 'label', header: 'Label' },
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }: { row: { original: PayComponent } }) => (
          <code className="text-xs text-muted-foreground">{row.original.slug}</code>
        ),
      },
      {
        accessorKey: 'component_type',
        header: 'Type',
        cell: ({ row }: { row: { original: PayComponent } }) => (
          <Badge variant="outline">{row.original.component_type.replace(/_/g, ' ')}</Badge>
        ),
      },
      {
        accessorKey: 'calc_kind',
        header: 'Calculation',
        cell: ({ row }: { row: { original: PayComponent } }) => (
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {row.original.calc_kind.replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        accessorKey: 'formula',
        header: 'Formula',
        cell: ({ row }: { row: { original: PayComponent } }) =>
          row.original.formula ? (
            <code className="text-xs">{row.original.formula}</code>
          ) : (
            <span className="text-xs text-muted-foreground">
              {CALC_KIND_NOTES[row.original.calc_kind] || '-'}
            </span>
          ),
      },
      { accessorKey: 'rounding_mode', header: 'Rounding' },
    ],
    [],
  )

  async function onSave() {
    if (!form.slug || !form.label) {
      toast({ title: 'Slug + label required', variant: 'destructive' })
      return
    }
    const payload: Record<string, unknown> = {
      slug: form.slug,
      label: form.label,
      component_type: form.component_type,
      calc_kind: form.calc_kind,
      rounding_mode: form.rounding_mode,
      visible_on_payslip: form.visible_on_payslip,
    }
    if (form.calc_kind === 'formula') payload.formula = form.formula
    if (form.calc_kind === 'fixed' && form.fixed_amount_minor_units) {
      payload.fixed_amount_minor_units = Number(form.fixed_amount_minor_units)
      payload.fixed_currency = 'USD'
    }
    if (form.calc_kind === 'percentage' && form.rate_pct) {
      payload.rate_pct = Number(form.rate_pct)
    }
    try {
      await createMut.mutateAsync(payload as never)
      toast({ title: 'Component added' })
      setOpen(false)
      setForm({
        slug: '',
        label: '',
        component_type: 'earning',
        calc_kind: 'fixed',
        formula: '',
        fixed_amount_minor_units: '',
        rate_pct: '',
        rounding_mode: 'HALF_EVEN',
        visible_on_payslip: true,
      })
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    }
  }

  return (
    <Page
      title="Pay Components & Formulas"
      description="The reusable library of pay components. Formula components are evaluated in a sandboxed AST DSL - no eval, no property access, only whitelisted operators and builtins (min, max, round, if, in_range, sum)."
      actions={
        canManage ? (
          <Toolbar>
            <Button variant="secondary" onClick={onSetupUs} disabled={setupMut.isPending}>
              <Wand2 className="mr-2 size-4" /> {setupMut.isPending ? 'Setting up…' : 'Set up US payroll'}
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="mr-2 size-4" /> New component
            </Button>
          </Toolbar>
        ) : undefined
      }
    >
      <Toolbar align="start">
        <FormField
          label=""
          control={
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 w-56 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All types</option>
              {COMPONENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          }
        />
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={data?.data ?? []}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Calculator}
            title="No pay components yet"
            description="Components are the building blocks of every payslip. Use 'Set up US payroll' to seed the standard earnings + FICA / Medicare / FUTA / withholding components, then tweak as needed."
            action={
              canManage ? (
                <Toolbar>
                  <Button variant="secondary" onClick={onSetupUs} disabled={setupMut.isPending}>
                    <Wand2 className="mr-2 size-4" /> {setupMut.isPending ? 'Setting up…' : 'Set up US payroll'}
                  </Button>
                  <Button onClick={() => setOpen(true)}>
                    <Plus className="mr-2 size-4" /> New component
                  </Button>
                </Toolbar>
              ) : undefined
            }
          />
        }
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="New pay component"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={createMut.isPending}>
              {createMut.isPending ? 'Saving…' : 'Save component'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Slug"
              required
              hint="Used as the formula identifier (lowercase, underscores)."
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9_]/g, '').toLowerCase() })}
            />
            <FormField
              label="Label"
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Type"
              control={
                <select
                  value={form.component_type}
                  onChange={(e) => setForm({ ...form, component_type: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {COMPONENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              }
            />
            <FormField
              label="Calculation"
              control={
                <select
                  value={form.calc_kind}
                  onChange={(e) => setForm({ ...form, calc_kind: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {CALC_KINDS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              }
            />
          </div>
          {form.calc_kind === 'fixed' ? (
            <FormField
              label="Fixed amount (minor units / cents)"
              inputMode="numeric"
              value={form.fixed_amount_minor_units}
              onChange={(e) => setForm({ ...form, fixed_amount_minor_units: e.target.value.replace(/\D/g, '') })}
            />
          ) : null}
          {form.calc_kind === 'formula' ? (
            <FormField
              label="Formula"
              hint="Example: round((regular_pay + overtime_pay) * 0.062, 2)"
              value={form.formula}
              onChange={(e) => setForm({ ...form, formula: e.target.value })}
            />
          ) : null}
          {form.calc_kind === 'percentage' ? (
            <FormField
              label="Rate %"
              inputMode="decimal"
              value={form.rate_pct}
              onChange={(e) => setForm({ ...form, rate_pct: e.target.value.replace(/[^\d.]/g, '') })}
            />
          ) : null}
          <FormField
            label="Rounding mode"
            control={
              <select
                value={form.rounding_mode}
                onChange={(e) => setForm({ ...form, rounding_mode: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="HALF_EVEN">HALF_EVEN (banker&apos;s, US default)</option>
                <option value="HALF_UP">HALF_UP</option>
                <option value="HALF_DOWN">HALF_DOWN</option>
                <option value="UP">UP</option>
                <option value="DOWN">DOWN</option>
              </select>
            }
          />
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="component-visible-payslip"
              checked={form.visible_on_payslip}
              onCheckedChange={(v) => setForm({ ...form, visible_on_payslip: v === true })}
            />
            <label htmlFor="component-visible-payslip">Visible on the payslip</label>
          </div>
        </div>
      </Modal>
    </Page>
  )
}
